import { audio } from '../audio/AudioManager';
import type { AudioCue } from '../audio/types';
import { haptics, type HapticEvent } from './haptics';

export type FeedbackEvent =
  | 'ui.click'
  | 'ui.confirm'
  | 'ui.invalid'
  | 'game.slide'
  | 'game.merge'
  | 'game.spawn'
  | 'match.countdown'
  | 'match.go'
  | 'match.combo'
  | 'attack.charge'
  | 'attack.impact'
  | 'match.ko'
  | 'match.victory'
  | 'match.defeat'
  | 'achievement.unlock'
  | 'system.error';

interface FeedbackDefinition {
  audio: AudioCue;
  haptic?: HapticEvent;
}

export const FEEDBACK: Readonly<Record<FeedbackEvent, FeedbackDefinition>> = {
  'ui.click': { audio: 'ui-click', haptic: 'click' },
  'ui.confirm': { audio: 'ui-confirm', haptic: 'confirm' },
  'ui.invalid': { audio: 'ui-invalid', haptic: 'invalid' },
  'game.slide': { audio: 'slide' },
  'game.merge': { audio: 'merge', haptic: 'merge' },
  'game.spawn': { audio: 'spawn' },
  'match.countdown': { audio: 'countdown' },
  'match.go': { audio: 'go', haptic: 'confirm' },
  'match.combo': { audio: 'combo', haptic: 'merge' },
  'attack.charge': { audio: 'attack-charge', haptic: 'power' },
  'attack.impact': { audio: 'attack-impact', haptic: 'impact' },
  'match.ko': { audio: 'ko', haptic: 'ko' },
  'match.victory': { audio: 'victory', haptic: 'confirm' },
  'match.defeat': { audio: 'defeat' },
  'achievement.unlock': { audio: 'achievement', haptic: 'confirm' },
  'system.error': { audio: 'error', haptic: 'invalid' },
};

export interface FeedbackEmitOptions {
  /** Play the mapped audio cue. Defaults to true. */
  audio?: boolean;
  /** Trigger the mapped haptic pattern. Defaults to true. */
  haptic?: boolean;
}

export const feedback = {
  emit(event: FeedbackEvent, options: FeedbackEmitOptions = {}): void {
    const definition = FEEDBACK[event];
    if (options.audio !== false) audio.play(definition.audio);
    if (options.haptic !== false && definition.haptic) haptics.trigger(definition.haptic);
  },
};

/** One delegated listener covers present and future Svelte buttons. */
export function installUiFeedback(root: Document = document): () => void {
  const onClick = (event: MouseEvent): void => {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target || target.hasAttribute('disabled') || target.dataset.feedback === 'none') return;
    feedback.emit('ui.click');
  };
  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick);
}
